import { useQuery } from "@tanstack/react-query";
import { apiService } from "../../services/api";
import { DashboardStats as DashboardStatsType } from "../../types/api";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Loader2,
  Users,
  Building2,
  MessageSquare,
  AlertCircle,
} from "lucide-react";
import { SuccessResponse } from "../../types/generated";

const StatCard = ({
  title,
  icon: Icon,
  isLoading,
  error,
  mainStat,
  subStats,
}: {
  title: string;
  icon: React.ElementType;
  isLoading: boolean;
  error: Error | null;
  mainStat?: number;
  subStats?: { label: string; value?: number }[];
}) => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-sm font-medium">{title}</CardTitle>
      <Icon className="h-4 w-4 text-muted-foreground" />
    </CardHeader>
    <CardContent>
      {isLoading ? (
        <div className="flex justify-center">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <div className="flex items-center text-xs text-destructive">
          <AlertCircle className="h-3 w-3 mr-1" />
          Failed to load stats
        </div>
      ) : (
        <>
          <div className="text-2xl font-bold">{mainStat || 0}</div>
          {subStats && (
            <div className="text-xs text-muted-foreground">
              {subStats.map((stat, index) => (
                <span key={stat.label}>
                  {stat.value || 0} {stat.label}
                  {index < subStats.length - 1 ? " · " : ""}
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </CardContent>
  </Card>
);

export const DashboardStats = () => {
  const {
    data: statsResponse,
    isLoading,
    error,
  } = useQuery<SuccessResponse<DashboardStatsType>, Error>({
    queryKey: ["admin-dashboard-stats"],
    queryFn: () => apiService.getDashboardStats(),
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const stats = statsResponse?.data;

  return (
    <div className="grid gap-4 md:grid-cols-3">
      <StatCard
        title="Users"
        icon={Users}
        isLoading={isLoading}
        error={error}
        mainStat={stats?.users_stats.total_users}
        subStats={[
          { label: "active", value: stats?.users_stats.active_users },
          { label: "pending", value: stats?.users_stats.pending_users },
        ]}
      />

      <StatCard
        title="Accounts"
        icon={Building2}
        isLoading={isLoading}
        error={error}
        mainStat={stats?.accounts_stats.total_accounts}
        subStats={[
          { label: "active", value: stats?.accounts_stats.active_accounts },
          { label: "trial", value: stats?.accounts_stats.trial_accounts },
        ]}
      />

      <StatCard
        title="Messages"
        icon={MessageSquare}
        isLoading={isLoading}
        error={error}
        mainStat={stats?.messages_stats.total_messages}
        subStats={[
          { label: "today", value: stats?.messages_stats.messages_today },
          { label: "pending", value: stats?.messages_stats.pending_messages },
        ]}
      />
    </div>
  );
};
